import { App, message as messageStatic } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { VestiaireFormState } from '@/features/productSearch/vestiaire/vestiaireCriteria';
import {
  type VestiaireUrlIgnoredPart,
  countFilledCriteria,
  isUrlImportPending,
  urlImportErrorMessage,
} from '@/features/productSearch/vestiaire/vestiaireUrlParts';
import { useParseVestiaireSearchUrlQuery } from '@/shared/hooks/queries/generated';

// ============================================================================
// État de l'import d'URL Vestiaire Collective (champ, erreur, parties non reprises), tenu par
// useVestiaireSearchForm pour que la modale puisse importer à l'envoi une URL collée mais pas
// importée — même comportement que Vinted. Lecture SERVEUR (parseVestiaireSearchUrl), contrat
// docs/superpowers/api-changelog/2026-10-01-vestiaire-import-url.md.
// ============================================================================

/** Issue de l'import implicite à l'envoi : formulaire importé à utiliser tel quel (l'état React ne
 * sera à jour qu'au rendu suivant). */
export type VestiairePendingImport =
  | { status: 'none' }
  | { status: 'imported'; form: VestiaireFormState }
  | { status: 'failed' };

export interface VestiaireUrlImportState {
  url: string;
  /** Nouvelle saisie : efface l'erreur et les parties non reprises de l'import précédent. */
  setUrl: (url: string) => void;
  error: string;
  ignored: VestiaireUrlIgnoredPart[];
  /** Une URL a déjà été importée avec succès. */
  imported: boolean;
  /** Import de l'URL saisie : le formulaire importé, ou null si elle n'a pas pu être lue (erreur affichée). */
  importUrl: () => Promise<VestiaireFormState | null>;
  /** À l'envoi : importe l'URL saisie si elle ne l'a pas encore été (`failed` = envoi à interrompre). */
  importPending: () => Promise<VestiairePendingImport>;
  reset: () => void;
}

export interface UseVestiaireUrlImportOptions {
  importCriteria: (criteria: Record<string, unknown>) => VestiaireFormState;
  rememberLabels: (type: 'brand' | 'model', options: { id: number; title: string }[]) => void;
  /** Arbre des catégories chargé : sans lui, les catégories importées seraient perdues en silence. */
  treeReady: boolean;
}

export const useVestiaireUrlImport = ({
  importCriteria,
  rememberLabels,
  treeReady,
}: UseVestiaireUrlImportOptions): VestiaireUrlImportState => {
  const { t } = useTranslation();
  const [url, setUrlState] = useState('');
  const [error, setError] = useState('');
  const [ignored, setIgnored] = useState<VestiaireUrlIgnoredPart[]>([]);
  // Dernière URL importée avec succès : l'envoi ne la relit pas.
  const [importedUrl, setImportedUrl] = useState<string | null>(null);
  // Même repli que SearchModal : l'API statique hors d'un <App> d'AntD.
  let appMessage: typeof messageStatic;
  try {
    appMessage = App.useApp().message as unknown as typeof messageStatic;
  } catch {
    appMessage = messageStatic;
  }

  const setUrl = useCallback((next: string) => {
    setUrlState(next);
    setError('');
    setIgnored([]);
  }, []);

  const importUrl = async (): Promise<VestiaireFormState | null> => {
    const trimmed = url.trim();
    if (!trimmed) return null;
    setError('');
    setIgnored([]);
    if (!treeReady) {
      setError(t('searchModal.urlReadError'));
      return null;
    }
    try {
      const { parseVestiaireSearchUrl: result } = await useParseVestiaireSearchUrlQuery.fetcher({ url: trimmed })();
      const form = importCriteria(result.criteria);
      rememberLabels('brand', result.brands);
      rememberLabels('model', result.models);
      setIgnored(result.ignored);
      setImportedUrl(trimmed);
      const count = countFilledCriteria(result.criteria);
      if (count > 0) appMessage.success(t('searchModal.urlImportSuccess', { count }));
      else appMessage.info(t('searchModal.urlImportNone'));
      return form;
    } catch (caught) {
      setError(urlImportErrorMessage(caught, t));
      return null;
    }
  };

  const importPending = async (): Promise<VestiairePendingImport> => {
    if (!isUrlImportPending(url, importedUrl)) return { status: 'none' };
    const form = await importUrl();
    return form ? { status: 'imported', form } : { status: 'failed' };
  };

  const reset = useCallback(() => {
    setUrlState('');
    setError('');
    setIgnored([]);
    setImportedUrl(null);
  }, []);

  return { url, setUrl, error, ignored, imported: importedUrl != null, importUrl, importPending, reset };
};
